// tslint:disable: max-line-length
import ControlInput from 'lqa-ui/lib/Components/ControlInput';
import Icon from 'lqa-ui/lib/Components/Icon';
import React, { useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import { useNavigate } from 'react-router-dom';
import LoginLogo from '../../components/commons/icons/login_logo';
import { LevelTypes } from '../../enums/Level';
import { handleBrandAndHotelData } from '../../helpers/handleBrandAndHotelData';
import { sentryReport } from '../../helpers/sentry';
import { showErrorMessage } from '../../helpers/showMessage';
import { ClearAllIndexedDB } from '../../indexed';
import { loginUser } from '../../requests/login';
import Analytics from '../../utils/analytics';
import { HTTP_CODE, SUPPORT_URL } from '../../utils/constants';
import { setItem } from '../../utils/localStorage';
import { getFeatureFlags } from './../../app/common/requets/getFeatureFlags';
import { ButtonLarge } from './../../app/common/sharedComponents/buttons/ButtonLarge';
import { Modal } from './../../app/common/sharedComponents/Modal';
import { ButtonBlockColumn } from './../../app/common/sharedStyledComponents/GenericStyles.styled';
import { PhoneContainer } from './../../app/common/sharedStyledComponents/PhoneInput.styled';
import { Body4, Body5 } from './../../app/common/typography';
import {
  AuthNotificationMessage,
  CTASection,
  ForgotPasswordHeader,
  HeaderSection,
  LoginContainer,
  LoginForm,
  RegisterContainer,
  Subtitle,
  TermsOfServicesWRapper,
  TextBold,
  TextLogged,
} from './styled.login';

interface InputValuesTypes {
  username: string;
  userphone: number | string;
  password: string;
}

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  setItem('hotelList', []);
  const [inputValues, setInputValues] = useState<InputValuesTypes>({
    username: '',
    userphone: '',
    password: '',
  });
  const [phoneCountryCode, setPhoneCountryCode] = useState('1');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loginErrorState, setLoginErrorState] = useState(false);
  const [validatingLogin, setValidatingLogin] = useState(false);
  const [errorWarning, setErrorWarning] = useState<boolean>(false);
  const lowerLevels: LevelTypes[] = ['staff', 'teamLead'];
  const isIosApp: boolean = navigator.userAgent.includes('iPhone');

  const handleForgotPassword = () => {
    navigate('/forgotpassword');
  };

  const handleRegister = () => {
    navigate('/register/activationCode');
  };

  const signInHandler = async (event, isLogoutOther) => {
    localStorage.removeItem('day');
    ClearAllIndexedDB();

    if (
      inputValues.password === '' ||
      (!inputValues.username && !inputValues.userphone) ||
      (inputValues.username && inputValues.userphone) ||
      (!inputValues.username &&
        inputValues.userphone &&
        String(inputValues.userphone).length < 10)
    ) {
      showErrorMessage('Please enter a valid login details');
      return;
    }
    event.preventDefault();
    setValidatingLogin(true);
    try {
      const authResponse: any = await loginUser(
        inputValues.username,
        inputValues.userphone,
        inputValues.password,
        isLogoutOther
      );
      setItem('user', JSON.stringify(authResponse.data));
      // Store the raw string (no JSON.stringify) so getItem round-trips it unchanged.
      setItem('sessionId', authResponse.data.sessionId);
      if (inputValues.userphone) {
        setItem('login-phone', inputValues.userphone);
      } else if (inputValues.username) {
        setItem('login-email', inputValues.username.trim().toLowerCase());
      }

      const userData = authResponse.data;
      if (
        userData &&
        userData.user &&
        userData.user.brands &&
        userData.user.brands.length
      ) {
        handleBrandAndHotelData(userData.user.brands);
      }

      if (authResponse.data && authResponse.data.userProfile) {
        setItem('userProfile', JSON.stringify(authResponse.data.userProfile));
      }
      getFeatureFlags();

      setTimeout(() => {
        if (
          authResponse.data &&
          authResponse.data.user &&
          authResponse.data.user.level
        ) {
          const userId: number = authResponse.data.user.id;
          let externalParams: string = '';
          if (isIosApp) {
            externalParams = `?userId=${userId}`;
          }
          if (lowerLevels.includes(authResponse.data.user.level)) {
            window.location.href = '/myactions' + externalParams;
          } else {
            window.location.href = '/dashboard' + externalParams;
          }
        } else {
          window.location.href = '/dashboard';
        }
      }, 1000);
    } catch (err: any) {
      function userType (inputs: InputValuesTypes) {
        if (inputs.username === '') {
          return { userphone: inputs.userphone };
        } else {
          return { username: inputs.username };
        }
      }

      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.statusCode === HTTP_CODE.methodNotAllowed
      ) {
        InActiveUserNotification();

        sentryReport(
          err.response.data,
          err.message,
          err.code,
          {
            userInfo: userType(inputValues),
            InActiveUserNotification: 'displayed',
          },
          inputValues.username
        );
      }

      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.statusCode === HTTP_CODE.forbiddenCode
      ) {
        setShowAccountNotFoundModal(err.response.data.message);

        sentryReport(
          err.response.data,
          err.message,
          err.code,
          {
            userInfo: userType(inputValues),
            setShowAccountNotFoundModal: 'displayed',
          },
          inputValues.username
        );
      }

      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.statusCode === 409
      ) {
        setIsOpen(true);

        Analytics.sendEvent('multiple-session-found');
      }

      setLoginErrorState(true);

      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.statusCode === HTTP_CODE.unauthorized
      ) {
        setTimeout(() => {
          setLoginErrorState(false);
          setErrorWarning(true);

          sentryReport(
            err.response.data,
            err.message,
            err.code,
            {
              userInfo: userType(inputValues),
            },
            inputValues.username
          );
        }, 1000);
      }
    }
    setValidatingLogin(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputValues({ ...inputValues, [name]: value });
  };

  const handleOnChangePhone = (value: string, data: any): any => {
    setPhoneCountryCode(data.dialCode || '1');
    if (phoneCountryCode !== data.dialCode) {
      setInputValues({ ...inputValues, userphone: '' });
      return;
    }
    if (value.slice(data.dialCode.length)) {
      setInputValues({ ...inputValues, userphone: value });
    } else {
      setInputValues({
        ...inputValues,
        userphone: value.slice(data.dialCode.length),
      });
    }
  };

  const handlePhoneInputBlur = (): void => {
    const { userphone } = inputValues;
    if (phoneCountryCode === '44' && userphone) {
      const rawPhoneNumber = userphone
        .toString()
        .slice(phoneCountryCode.length);
      const cleanedPhoneNumber = `${phoneCountryCode}${rawPhoneNumber.replace(/^0/, '')}`;
      setInputValues({ ...inputValues, userphone: cleanedPhoneNumber });
    }
  };

  const InActiveUserNotification = () =>
    confirmAlert({
      overlayClassName: 'overlayPrompt',
      customUI: ({ onClose }) => {
        return (
          <div className="backOverlay">
            <span className="d-block  text-right mb-20 " onClick={onClose}>
              <Icon kind="close" height={17} width={17} />
            </span>
            <p className="sans-font font-normal fw-400 f-15 lh-20 text-center color-black">
              Your account has been deactivated. If you set up a myLQA
              membership, you may log in with those credentials. If you feel you
              were deactivated in error, please get in touch with your Hotel
              administrator
            </p>
          </div>
        );
      },
    });

  const setShowAccountNotFoundModal = (message) =>
    confirmAlert({
      overlayClassName: 'overlayPrompt',
      customUI: ({ onClose }) => {
        return (
          <div className="backOverlay">
            <span className="d-block  text-right " onClick={onClose}>
              <Icon kind="close" height={17} width={17} />
            </span>
            <h1 className="sans-font font-normal fw-600 f-20 lh-28 text-center color-black">
              {message === 'noSubscription'
                ? 'No Active Subscription'
                : 'myLQA Account Not Found'}{' '}
            </h1>
            <p className="sans-font font-normal fw-400 f-15 lh-20 text-center color-black">
              It seems that you don't have access to the myLQA app with your
              current user profile. Contact your system administrator to set up
              or activate your account. For further assistance, reach out to{' '}
              <a className="support-link">{SUPPORT_URL}</a>
            </p>
          </div>
        );
      },
    });

  return (
    <>
      <LoginContainer>
        <HeaderSection>
          <LoginLogo width={174} height={80} />
          <Subtitle>Own your Hospitality Journey</Subtitle>
        </HeaderSection>
        <LoginForm>
          <ControlInput
            label="Email Address"
            name="username"
            value={inputValues.username}
            onChange={handleChange}
            required={true}
            hasError={loginErrorState}
            disabled={validatingLogin || !!inputValues.userphone}
            type="email"
          />
          <div className="text-center color-8298B5">or</div>
          <PhoneContainer>
            <PhoneInput
              country={'us'}
              value={inputValues.userphone.toString() || phoneCountryCode}
              dropdownClass={'phone-input-dropdown'}
              containerClass={'phone-input-dropdown'}
              countryCodeEditable={false}
              onChange={handleOnChangePhone}
              enableLongNumbers={true}
              disabled={validatingLogin || !!inputValues.username}
              onBlur={handlePhoneInputBlur}
            />
          </PhoneContainer>
          <ControlInput
            label="Password"
            name="password"
            value={inputValues.password}
            onChange={handleChange}
            required={true}
            secureTextEntry={true}
            revealPasswordAddOn={true}
            hasError={loginErrorState}
            disabled={validatingLogin}
          />
          {loginErrorState ||
            (errorWarning && (
              <AuthNotificationMessage>
                Does not match our records. Review or{' '}
                <span className="fw-600" onClick={handleForgotPassword}>
                  Forgot Password?
                </span>
              </AuthNotificationMessage>
            ))}
          <ButtonLarge
            title={t('COMMON.OUTSIDE.BUTTONS.SIGN_IN')}
            onClick={(event) => signInHandler(event, false)}
            backgroundColor="oxfordBlue"
            loading={validatingLogin}
          />
          <ForgotPasswordHeader onClick={handleForgotPassword}>
            Forgot Password?
          </ForgotPasswordHeader>
          <RegisterContainer onClick={handleRegister}>
            <Body4>
              {t('COMMON.SELF_ASSESSMENT.BUTTON.OR_REGISTER')}
            </Body4>
          </RegisterContainer>
        </LoginForm>
        <CTASection />
        <TermsOfServicesWRapper />
      </LoginContainer>
      {isOpen && (
        <Modal isOpen={isOpen}>
          <div>
            <TextLogged>
              <Body5>
                You are currently logged in on another device. To continue here,
                please choose <TextBold>'End Session'</TextBold> to terminate
                the session on the other device. All unsaved data will be lost.
                Select <TextBold>'Cancel'</TextBold> to remain logged in on your
                current device.
              </Body5>
            </TextLogged>
            <ButtonBlockColumn>
              <ButtonLarge
                title={t('COMMON.OUTSIDE.BUTTONS.END_SESSION')}
                onClick={(event) => {
                  signInHandler(event, true);
                  Analytics.sendEvent('multiple-session-found_end-session');
                }}
                backgroundColor="oxfordBlue"
                loading={validatingLogin}
              />
              <ButtonLarge
                title={t('COMMON.OUTSIDE.BUTTONS.CANCEL')}
                onClick={() => {
                  setIsOpen(false);
                  Analytics.sendEvent('multiple-session-found_cancel');
                }}
                outlined={true}
                inverted={true}
              />
            </ButtonBlockColumn>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Login;
